import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

interface ResetStudentViewInput {
  componentId: string;
  courseId: string;
}

interface ResetStudentViewPayload {
  component_id: string;
  course_id: string;
  user_id: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

/**
 * Deletes the student-view test account's stats for one component so it can be
 * experienced from scratch. Called from inside the masked student-view tab, so
 * the request identity IS the test account; the backend authorizes it via the
 * staff member embedded in the account id and only ever deletes that test
 * account's own stats — real student data can never be touched.
 */
export function useResetStudentView() {
  const { getIdToken, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, ResetStudentViewInput>({
    mutationFn: async ({ componentId, courseId }) => {
      const kuduUserId = getKuduUserId(user);

      if (!user || !kuduUserId) {
        throw createDataError('You must be signed in to reset student view.');
      }

      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () => {
          window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
        },
      });

      await client.invokeKuduMethod<
        unknown | LegacyKuduResponseEnvelope<unknown>,
        ResetStudentViewPayload
      >('ResetStudentViewComponent', {
        component_id: componentId,
        course_id: courseId,
        user_id: kuduUserId,
      });
    },
    onSuccess: async (_data, { componentId, courseId }) => {
      // The reset wiped this tab's test-account stats server-side; refetch the
      // component so the student view reflects the cleared answers/attempts/
      // scores without a manual page refresh. The key prefix covers every
      // component query variant (instructor flag, settings-scoped keys).
      await queryClient.invalidateQueries({
        queryKey: ['component', courseId, componentId],
      });
    },
  });
}
